import { AntDesign } from '@expo/vector-icons';
import { FirebaseAuthTypes, getIdToken, signOut as signOutMobile } from '@react-native-firebase/auth';
import { AxiosError } from 'axios';
import { router, useNavigation } from 'expo-router';
import { getIdToken as getIdTokenWeb, User } from 'firebase/auth';
import { Auth, signOut as signOutWeb } from 'firebase/auth';
import { useEffect, useLayoutEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { client } from '@/api';
import { useGetMeMutation, useLinkMe } from '@/api/hooks/meHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import GoBackButton from '@/components/buttons/GoBackButton';
import FormWrapper from '@/components/forms/FormWrapper';
import TextInputField from '@/components/inputs/TextInputField';
import Text from '@/components/Text';
import { SIGN_IN_BUTTON_BOTTOM } from '@/constants/layout';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useMeFavorites } from '@/contexts/favoritesContext';
import { auth } from '@/firebaseConfig';
import { useColor } from '@/hooks/useColor';
import { SignInData, signInResolver } from '@/lib/validations/AuthSchema';
import { i18n } from '@/utils/i18n';
import { useDeepLinkStore } from '@/deepLinks/deepLinkStore';

const ProfileScreen = () => {
  const colors = useColor();
  const navigation = useNavigation();

  const { dispatch, state: authState, clearCurrentUser, setCurrentUser, setMeResponse } = useAuth();
  const { clearFavorites } = useMeFavorites();
  const { clearPendingLink } = useDeepLinkStore()

  const { showError } = useEndFlowModal();

  const { mutateAsync: linkMe, isPending: linkMePending } = useLinkMe();
  const { mutateAsync: getMe, isPending: getMePending } = useGetMeMutation();

  const titleText = i18n.t('sign_in.individual.profile.title');
  const buttonText = i18n.t('mix.next');
  const namePlaceHolderText = i18n.t('sign_in.individual.profile.name_placeholder');
  const lastNamePlaceHolderText = i18n.t('sign_in.individual.profile.last_placeholder');
  const validationDescriptionText = i18n.t('sign_in.individual.profile.validation');

  const defaultValues: SignInData = {
    name: '',
    lastName: ''
  };

  const {
    control,
    handleSubmit,
    trigger,
    formState: { isValid, errors },
    setError
  } = useForm<SignInData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: signInResolver
  });

  useEffect(() => {
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const onPressBack = async () => {
      dispatch({ type: 'SIGN_OUT' });
      clearCurrentUser();
      if (Platform.OS === 'web') {
        await signOutWeb(auth() as Auth);
      } else {
        await signOutMobile(auth() as FirebaseAuthTypes.Module);
      }
      clearFavorites();
      clearPendingLink();
      if (router.canDismiss()) router.dismissAll();
      router.push('/');
    };
    navigation.setOptions({
      headerShown: true,
      headerShadowVisible: false,
      headerBackVisible: false,
      headerTitle: () => <></>,
      headerLeft: () => <GoBackButton onPress={onPressBack} />
    });
  }, [clearCurrentUser, clearFavorites, clearPendingLink, dispatch, navigation]);

  const onSubmit: SubmitHandler<SignInData> = async (data) => {
    if (authState.userId) {
      try {
        const linkMeResponse = await linkMe({
          firstName: data.name,
          lastName: data.lastName,
          role: 'INDIVIDUAL',
          firebaseId: authState.userId,
          phone: authState.phone
        });

        const tokenPromise =
          Platform.OS === 'web'
            ? getIdTokenWeb(auth().currentUser as User, true)
            : getIdToken(auth().currentUser as FirebaseAuthTypes.User, true);

        const refreshedToken = await tokenPromise;
        // Force refresh of token to update token claims of firebase
        client.defaults.headers.common['Authorization'] = `Bearer ${refreshedToken}`;

        const meResponse = await getMe({ firebaseId: authState.userId });
        if (meResponse.user) {
          setCurrentUser(meResponse.user);
          setMeResponse(meResponse);
        }

        router.replace({
          pathname: '/login/individual/distances',
          params: {
            onBoardingId: linkMeResponse.onBoardingPreferenceId,
            buyingShow: authState.isBuyingShow ? 'true' : 'false'
          }
        });
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error(axiosError.response?.data);
        setError('root', { type: 'manual', message: axiosError.message });
        showError({ title: 'Error', message: `${axiosError.message}` });
      }
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <FormWrapper scrollEnable={false}>
        <View style={styles.contentWrapper}>
          <View>
            <Text style={[styles.titleText, { color: colors.titleText }]} fontWeight="700">
              {titleText}
            </Text>
            <View style={styles.formContainer}>
              <TextInputField name="name" control={control} placeholder={namePlaceHolderText} hideError={true} />
              <TextInputField
                name="lastName"
                control={control}
                placeholder={lastNamePlaceHolderText}
                hideError={true}
              />
              <Text>{validationDescriptionText}</Text>
              <View>
                <View style={styles.validationContent}>
                  {!errors.name ? (
                    <View style={styles.circle}>
                      <AntDesign name="check" size={15} color="black" />
                    </View>
                  ) : (
                    <View style={styles.circle} />
                  )}
                  <Text style={styles.validationText}>{namePlaceHolderText}</Text>
                </View>
                <View style={styles.validationContent}>
                  {!errors.lastName ? (
                    <View style={styles.circle}>
                      <AntDesign name="check" size={15} color="black" />
                    </View>
                  ) : (
                    <View style={styles.circle} />
                  )}
                  <Text style={styles.validationText}>{lastNamePlaceHolderText}</Text>
                </View>
              </View>
            </View>
          </View>

          <BrandedButton
            loading={linkMePending || getMePending}
            style={styles.btnStyle}
            onPress={handleSubmit(onSubmit)}
            disabled={!isValid}>
            {buttonText}
          </BrandedButton>
        </View>
      </FormWrapper>
    </SafeAreaView>
  );
};

export default ProfileScreen;

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%'
  },

  contentWrapper: {
    width: '100%',
    height: '100%',
    justifyContent: 'space-between'
  },

  titleText: {
    fontSize: 22,
    marginBottom: 50,
    textAlign: 'center',
    marginTop: 40
  },

  formContainer: {
    rowGap: 16
  },

  btnStyle: {
    width: '100%',
    marginBottom: SIGN_IN_BUTTON_BOTTOM
  },

  validationContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5
  },

  validationText: {
    marginLeft: 15
  },

  circle: {
    borderRadius: 12,
    height: 24,
    width: 24,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }
});
